import React, { useEffect, useState } from 'react'
import { Text, View } from "../../Components/Themed"
import type { ArticleProps, FocusPositionProps, MyPlayerProps, OrderProps, PostCommentProps, PostProps, PublicPlayerProps, TextTagProps } from "../../types"
import { SocialComponentApi, SocialComponentHelpers } from '../api'
import { ActivityIndicator, FlatList, Keyboard } from 'react-native'
import PostCard from '../PostCard'
import FormattedTextInput from '../FormattedTextInput'
import InputBar from '../InputBar'
import moment from 'moment-mini'
import PostCommentCard from './components/PostCommentCard'
import SocialOrdersList from '../SocialOrdersList'
import SocketManager from '../../Socket'
import { useColors } from '../../constants/useColors'

type ActivityDetailProps = {
    post_id:string,
    me?:MyPlayerProps,
    onSelectTag:(t:TextTagProps) => void,
    onCopyOrder?:(o:OrderProps) => void,
    onFadeOrder?:(o:OrderProps) => void,
    onSelectPlayer:(p:PublicPlayerProps) => void,
    onSelectArticle:(a:ArticleProps) => void,
    onSelectImage:(i:any) => void,
    onRequestAuthenticate:() => void,
    onFocusPosition?:(pos:FocusPositionProps) => void
}
const sections = ['post','comments']
const ActivityDetail = ({ post_id, me, onFocusPosition, onSelectArticle, onSelectImage, onSelectTag, onCopyOrder, onFadeOrder, onSelectPlayer, onRequestAuthenticate }:ActivityDetailProps) => {
    const Colors = useColors();
    const [size, setSize] = useState({ width:0, height:0 });
    const [ show_input_bar, setShowInputBar ] = useState(false);
    const [ needs_reload, setNeedsReload ] = useState(false);
    const [ socket_comment, setSocketComment ] = useState<PostCommentProps|undefined>(undefined);
    const [ post_refresh_key, setPostRefreshKey ] = useState(moment().toString())
    const [ comment_loading, setCommentLoading ] = useState(false);
    const [ draft_orders, setDraftOrders ] = useState<OrderProps[]>([]);
    const [ state, setState ] = useState<{
        loading: boolean,
        post?:PostProps,
        orders:OrderProps[],
        articles:ArticleProps[],
        players:PublicPlayerProps[],
        post_comments:PostCommentProps[],
    }>({
        loading:false,
        post_comments:[],
        articles:[],
        orders:[],
        players:[]
    });
    const { loading, post, post_comments, players, orders, articles } = state;
    const sorted_comments = post_comments.sort((a,b) =>{
        return moment(b.create_datetime).unix() - moment(a.create_datetime).unix()
    })
    useEffect(() => {
        SocialComponentApi.setEnvironment();
        getData(post_id)
    },[post_id, me?.player_id]);

    useEffect(() => {
        if(!socket_comment){ return }
        setState({
            ...state,
            post_comments: post_comments.filter(pc => pc.post_comment_id != socket_comment.post_comment_id).concat(socket_comment)
        })
    },[socket_comment?.post_comment_id])

    const getData = async(id:string) => {
        setState({ ...state, loading:true });
        const server_post = await SocialComponentApi.getPostById(id, 'memo');
        if(!server_post){ return alert('Unable to get activity at this time.  Please try again') }
        const comments = await SocialComponentApi.getPostComments(id, 0);
        let order_ids = server_post.memo_orders ?? []
        let comment_orders:string[] = []
        comments.filter(c => c.comment_orders).map(c => c.comment_orders?.map(o => comment_orders.push(o)))
        let ser_orders = await SocialComponentApi.getBulkOrders(order_ids.concat(comment_orders));
        let article_ids = server_post.memo_articles ?? []
        let ser_articles = await SocialComponentApi.getBulkArticles(article_ids);
        let player_ids = [ ...new Set(comments.map(c => c.player_id).concat(server_post.player_id)) ]
        let ser_players = await SocialComponentApi.getPlayersByIds(player_ids);
        setState({
            ...state,
            post: server_post,
            post_comments: comments,
            players: ser_players,
            orders:ser_orders,
            articles: ser_articles,
            loading:false
        });
    }


    const handleSendImage = async(image:any) => {
            if(!post || comment_loading){ return }
            setCommentLoading(true);
            const new_comment = SocialComponentHelpers.getEmptyComment();
            const pc = await SocialComponentApi.createComment({ 
                ...new_comment, 
                formatted_text: '',
                activity_post_id: post_id,
                tags: [],
                comment_images: [image],
                message: ''
            });
            if(!pc){
                setCommentLoading(false);
                return
            }
            setState({
                ...state,
                post_comments: post_comments.filter(m => m.post_comment_id != pc.post_comment_id).concat(pc)
            })
            setCommentLoading(false);
        }

    const handleSendComment = async(data: { formatted_text: string, text:string }, tags:TextTagProps[]) => {
        if(!post || comment_loading){ return }
        setCommentLoading(true);

        //Find urls!
        const url = SocialComponentHelpers.getUrlsFromString(data.text);
        let message_urls:string[] = []
        if(url){ message_urls.push(url) }
        const new_comment = SocialComponentHelpers.getEmptyComment();
        const pc = await SocialComponentApi.createComment({ 
            ...new_comment, 
            activity_post_id: post_id,
            formatted_text: data.formatted_text,
            tags: tags.map(t => { return { ...t, data:undefined } }), //Drop the data!
            comment_urls: message_urls,
            comment_orders:draft_orders.map(o => o.order_id),
            message: data.text 
        });
        if(!pc){
            setCommentLoading(false);
            return
        }
        setDraftOrders([]);
        setState({
            ...state,
            post_comments: post_comments.filter(m => m.post_comment_id != pc.post_comment_id).concat(pc)
        })
        Keyboard.dismiss();
        setCommentLoading(false);
    }

    const renderComments = (data:{ item:PostCommentProps, index:number }) => {
        const player = players.find(p => p.player_id == data.item.player_id);
        const comment_orders = data.item.comment_orders ? orders.filter(o => data.item.comment_orders?.includes(o.order_id)): []
        return (
            <PostCommentCard
                post_comment={data.item}
                player={player}
                orders={comment_orders}
                onSelectImage={onSelectImage}
                onSelectTag={onSelectTag}
                onCopyOrder={onCopyOrder}
                onFadeOrder={onFadeOrder}
            />
        )
    }

    const renderSections = (data:{ item:string, index:number }) => {
        switch(data.item){
            case 'post':
                if(!post){ return <></> }
                const player = players.find(p => p.player_id == post.player_id)
                return (
                    <PostCard
                        post={post}
                        refresh_key={post_refresh_key}
                        width={size.width}
                        //onViewComments={() => console.log('')}
                        player={player}
                        player_id={me?.player_id}
                        onCopyOrder={onCopyOrder}
                        onFadeOrder={onFadeOrder}
                        articles={articles}
                        orders={orders}
                        onRequestAuthenticate={onRequestAuthenticate}
                        onSelectArticle={onSelectArticle}
                        onSelectImage={onSelectImage}
                        onSelectPlayer={onSelectPlayer}
                        onSelectTag={onSelectTag}
                    />
                )
            case 'comments':
                return (
                    <FlatList
                        data={sorted_comments}
                        renderItem={renderComments}
                        key={'activity_comments_list'}
                        keyExtractor={item => item.post_comment_id.toString()}
                    />
                )
            default: return <></>
        }
    }

    return (
        <View style={{ flex:1 }} onLayout={(ev) => {
            const { width, height } = ev.nativeEvent.layout
            setSize({ width, height })
        }}>
            <View style={{ flex:1 }}>
                {loading ?
                <ActivityIndicator
                    style={{ padding:10, alignSelf:'center' }}
                    size={'large'}
                    color={Colors.text.h1}
                />
                :<></>}
                <FlatList
                    data={sections}
                    key={'activity_detail_list'}
                    keyExtractor={item => item}
                    renderItem={renderSections}
                />
            </View>
            {show_input_bar?
            <View type='header' style={{ padding:5 }}>
                <InputBar
                    actions={['image','action']}
                    width={size.width}
                    //insets={insets}
                    onTagOrder={(order) => {
                        setDraftOrders(draft_orders.filter(o => o.order_id != order.order_id).concat(order))
                    }}
                    onImageUpload={(image) => handleSendImage(image)}
                />
            </View>
            :<></>}
            {draft_orders.length > 0 ?
            <View>
                <Text theme='h2'>Tagged Orders</Text>
                <SocialOrdersList
                    players={players}
                    orders={draft_orders}
                    onUntagOrder={(order) => setDraftOrders(draft_orders.filter(o => o.order_id != order.order_id))}
                    direction='horizontal'
                />
            </View>
            :<></>}
            <View type='footer' style={{ padding:5 }}>
                <FormattedTextInput
                    submit_placement='inline'
                    icon_submit
                    width={size.width}
                    onSelectInput={() => setShowInputBar(!show_input_bar)}
                    onSubmit={(data, tags) => handleSendComment(data, tags)}
                    onFocusPosition={onFocusPosition}
                />
            </View>
            <SocketManager
                onConnect={() => {
                    if(needs_reload){
                        setPostRefreshKey(moment().toString());
                        getData(post_id);
                    }
                }}
                onDisconnect={() => setNeedsReload(true)}
                subscribed_events={['V1_UPDATE_POST_COMMENT_STAT', 'V1_UPDATE_POST_REACTION_STATS','UPDATE_POST_COMMENT']}
                onSocketEvent={(ev) => {
                    switch(ev.type){
                        case 'V1_UPDATE_POST_COMMENT_STAT':
                            console.log('updated')
                            setPostRefreshKey(moment().toString())
                            break
                        case 'V1_UPDATE_POST_REACTION_STATS':
                            setPostRefreshKey(moment().toString())
                            break
                        case 'UPDATE_POST_COMMENT':
                            const new_comment = ev.data as PostCommentProps
                            if(new_comment.activity_post_id != post_id){ return }
                            setSocketComment(new_comment)
                            break
                        default: return
                    }
                }}
            
            />
        </View>
    )
}

export default ActivityDetail